import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Box,
  Typography,
  Grid,
  InputAdornment,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Lock as PrivateIcon,
  People as FamilyIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { collection, addDoc, updateDoc, doc, serverTimestamp, getDocs, query, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import TagMembers from '../../Components/TagMembers';
import { notifyTaggedMembers } from '../../services/notificationService';
import logger from '../../utils/logger';

const CATEGORIES = [
  'Breakfast',
  'Lunch',
  'Dinner',
  'Appetizers',
  'Desserts',
  'Beverages',
  'Salads',
  'Soups',
  'Snacks',
  'Baking',
  'Other',
];

const DIFFICULTY_LEVELS = ['Easy', 'Medium', 'Hard'];

const VISIBILITY_OPTIONS = [
  { value: 'family', label: 'Entire Family', icon: <FamilyIcon />, description: 'All family members can view' },
  { value: 'private', label: 'Private', icon: <PrivateIcon />, description: 'Only you can view' },
  { value: 'shared', label: 'Shared With...', icon: <ShareIcon />, description: 'Select specific family members' },
];

const AddRecipeDialog = ({ open, onClose, onRecipeAdded, editRecipe = null }) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const isEditMode = !!editRecipe;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Other',
    prepTime: '',
    cookTime: '',
    servings: '',
    difficulty: 'Medium',
    visibility: 'family',
  });

  const [ingredients, setIngredients] = useState(['']);
  const [instructions, setInstructions] = useState(['']);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sharedWith, setSharedWith] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [taggedMembers, setTaggedMembers] = useState([]);

  // Load family members for sharing
  useEffect(() => {
    const loadFamilyMembers = async () => {
      if (!profile?.familyCode || formData.visibility !== 'shared') return;

      setLoadingMembers(true);
      try {
        const membersQuery = query(
          collection(db, 'users'),
          where('familyCode', '==', profile.familyCode)
        );
        const snapshot = await getDocs(membersQuery);
        const members = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(member => member.id !== currentUser.uid); // Exclude current user
        setFamilyMembers(members);
      } catch (err) {
        logger.error('Error loading family members:', err);
      } finally {
        setLoadingMembers(false);
      }
    };

    loadFamilyMembers();
  }, [profile?.familyCode, formData.visibility, currentUser?.uid]);

  // Initialize form when editing
  useEffect(() => {
    if (editRecipe && open) {
      setFormData({
        title: editRecipe.title || '',
        description: editRecipe.description || '',
        category: editRecipe.category || 'Other',
        prepTime: editRecipe.prepTime?.toString() || '',
        cookTime: editRecipe.cookTime?.toString() || '',
        servings: editRecipe.servings?.toString() || '',
        difficulty: editRecipe.difficulty || 'Medium',
        visibility: editRecipe.visibility || 'family',
      });
      setIngredients(editRecipe.ingredients?.length ? editRecipe.ingredients : ['']);
      setInstructions(editRecipe.instructions?.length ? editRecipe.instructions : ['']);
      setSharedWith(editRecipe.sharedWith || []);
      setTaggedMembers(editRecipe.taggedMembers || []);
      if (editRecipe.photoURL) {
        setPhotoPreview(editRecipe.photoURL);
      }
    } else if (!editRecipe && open) {
      // Reset form for new recipe
      resetForm();
    }
  }, [editRecipe, open]);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'Other',
      prepTime: '',
      cookTime: '',
      servings: '',
      difficulty: 'Medium',
      visibility: 'family',
    });
    setIngredients(['']);
    setInstructions(['']);
    setPhotoFile(null);
    setPhotoPreview(null);
    setSharedWith([]);
    setTaggedMembers([]);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVisibilityChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, visibility: value }));
    if (value !== 'shared') {
      setSharedWith([]);
    }
  };

  const handleSharedWithChange = (event) => {
    const value = event.target.value;
    setSharedWith(typeof value === 'string' ? value.split(',') : value);
  };

  const handleIngredientChange = (index, value) => {
    const newIngredients = [...ingredients];
    newIngredients[index] = value;
    setIngredients(newIngredients);
  };

  const addIngredient = () => {
    setIngredients([...ingredients, '']);
  };

  const removeIngredient = (index) => {
    if (ingredients.length > 1) {
      setIngredients(ingredients.filter((_, i) => i !== index));
    }
  };

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...instructions];
    newInstructions[index] = value;
    setInstructions(newInstructions);
  };

  const addInstruction = () => {
    setInstructions([...instructions, '']);
  };

  const removeInstruction = (index) => {
    if (instructions.length > 1) {
      setInstructions(instructions.filter((_, i) => i !== index));
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 100 * 1024 * 1024) {
        setError('File size must be less than 100MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file');
        return;
      }

      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Recipe title is required');
      return false;
    }

    if (!formData.description.trim()) {
      setError('Recipe description is required');
      return false;
    }

    const validIngredients = ingredients.filter((ing) => ing.trim());
    if (validIngredients.length === 0) {
      setError('At least one ingredient is required');
      return false;
    }

    const validInstructions = instructions.filter((inst) => inst.trim());
    if (validInstructions.length === 0) {
      setError('At least one instruction step is required');
      return false;
    }

    if (!formData.prepTime || formData.prepTime <= 0) {
      setError('Prep time must be greater than 0');
      return false;
    }

    if (!formData.cookTime || formData.cookTime <= 0) {
      setError('Cook time must be greater than 0');
      return false;
    }

    if (!formData.servings || formData.servings <= 0) {
      setError('Servings must be greater than 0');
      return false;
    }

    if (formData.visibility === 'shared' && sharedWith.length === 0) {
      setError('Please select at least one family member to share with');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setError('');

    if (!validateForm()) {
      return;
    }

    if (!profile?.familyCode) {
      setError('You must be part of a family to add recipes');
      return;
    }

    setLoading(true);

    try {
      let photoURL = editRecipe?.photoURL || '';

      // Upload photo if provided (new photo)
      if (photoFile) {
        const photoRef = ref(
          storage,
          `families/${profile.familyCode}/recipes/${Date.now()}_${photoFile.name}`
        );
        await uploadBytes(photoRef, photoFile);
        photoURL = await getDownloadURL(photoRef);
      }

      // Filter out empty ingredients and instructions
      const validIngredients = ingredients.filter((ing) => ing.trim());
      const validInstructions = instructions.filter((inst) => inst.trim());

      // Build recipe data
      const recipeData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        ingredients: validIngredients,
        instructions: validInstructions,
        prepTime: parseInt(formData.prepTime),
        cookTime: parseInt(formData.cookTime),
        servings: parseInt(formData.servings),
        difficulty: formData.difficulty,
        photoURL,
        visibility: formData.visibility,
        sharedWith: formData.visibility === 'shared' ? sharedWith : [],
        taggedMembers: taggedMembers || [],
        updatedAt: serverTimestamp(),
      };

      if (isEditMode) {
        // Update existing recipe
        await updateDoc(
          doc(db, 'families', profile.familyCode, 'recipes', editRecipe.id),
          recipeData
        );
      } else {
        // Create new recipe
        recipeData.createdBy = profile.displayName || currentUser.email;
        recipeData.createdById = currentUser.uid;
        recipeData.createdAt = serverTimestamp();

        const docRef = await addDoc(
          collection(db, 'families', profile.familyCode, 'recipes'),
          recipeData
        );

        // Send notifications to tagged members
        if (taggedMembers && taggedMembers.length > 0) {
          await notifyTaggedMembers(profile.familyCode, taggedMembers, {
            type: 'recipe',
            id: docRef.id,
            title: formData.title,
            createdBy: currentUser.uid,
            createdByName: profile.displayName || currentUser.email,
            actionUrl: '/recipes'
          });
        }
      }

      // Reset form and close
      resetForm();

      // Notify parent and close
      if (onRecipeAdded) {
        onRecipeAdded();
      }
      onClose();
    } catch (err) {
      logger.error('Error saving recipe:', err);
      setError(`Failed to ${isEditMode ? 'update' : 'add'} recipe. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Recipe' : 'Add New Recipe'}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Title */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Recipe Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="e.g., Grandma's Apple Pie"
            />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              multiline
              rows={3}
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Brief description of the recipe..."
            />
          </Grid>

          {/* Category */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
            >
              {CATEGORIES.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Difficulty */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Difficulty"
              name="difficulty"
              value={formData.difficulty}
              onChange={handleInputChange}
            >
              {DIFFICULTY_LEVELS.map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Prep Time */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              type="number"
              label="Prep Time"
              name="prepTime"
              value={formData.prepTime}
              onChange={handleInputChange}
              InputProps={{
                endAdornment: <InputAdornment position="end">min</InputAdornment>,
              }}
              inputProps={{ min: 1 }}
            />
          </Grid>

          {/* Cook Time */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              type="number"
              label="Cook Time"
              name="cookTime"
              value={formData.cookTime}
              onChange={handleInputChange}
              InputProps={{
                endAdornment: <InputAdornment position="end">min</InputAdornment>,
              }}
              inputProps={{ min: 1 }}
            />
          </Grid>

          {/* Servings */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              required
              type="number"
              label="Servings"
              name="servings"
              value={formData.servings}
              onChange={handleInputChange}
              inputProps={{ min: 1 }}
            />
          </Grid>

          {/* Privacy Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="h6" sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PrivateIcon fontSize="small" />
              Recipe Privacy
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Control who can see this recipe
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Who can see this recipe?</InputLabel>
              <Select
                value={formData.visibility}
                label="Who can see this recipe?"
                onChange={handleVisibilityChange}
              >
                {VISIBILITY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {option.icon}
                      <Box>
                        <Typography variant="body2">{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Share with specific members */}
          {formData.visibility === 'shared' && (
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Share with family members</InputLabel>
                <Select
                  multiple
                  value={sharedWith}
                  onChange={handleSharedWithChange}
                  input={<OutlinedInput label="Share with family members" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((userId) => {
                        const member = familyMembers.find(m => m.id === userId);
                        return (
                          <Chip
                            key={userId}
                            label={member?.displayName || member?.email || userId}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                  disabled={loadingMembers}
                >
                  {loadingMembers ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Loading family members...
                    </MenuItem>
                  ) : familyMembers.length === 0 ? (
                    <MenuItem disabled>No other family members found</MenuItem>
                  ) : (
                    familyMembers.map((member) => (
                      <MenuItem key={member.id} value={member.id}>
                        <Checkbox checked={sharedWith.indexOf(member.id) > -1} />
                        <ListItemText
                          primary={member.displayName || member.email}
                          secondary={member.relationship || 'Family Member'}
                        />
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Tag Members */}
          <Grid item xs={12}>
            <TagMembers
              familyCode={profile?.familyCode}
              value={taggedMembers}
              onChange={(members) => setTaggedMembers(members)}
              label="Tag Family Members"
              helperText="Tagged members will receive a notification about this recipe"
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Ingredients Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
              Ingredients
            </Typography>
            {ingredients.map((ingredient, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  label={`Ingredient ${index + 1}`}
                  value={ingredient}
                  onChange={(e) => handleIngredientChange(index, e.target.value)}
                  placeholder="e.g., 2 cups all-purpose flour"
                />
                <IconButton
                  onClick={() => removeIngredient(index)}
                  disabled={ingredients.length === 1}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={addIngredient}
              variant="outlined"
              size="small"
            >
              Add Ingredient
            </Button>
          </Grid>

          {/* Instructions Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Instructions
            </Typography>
            {instructions.map((instruction, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label={`Step ${index + 1}`}
                  value={instruction}
                  onChange={(e) => handleInstructionChange(index, e.target.value)}
                  placeholder="Describe this step..."
                />
                <IconButton
                  onClick={() => removeInstruction(index)}
                  disabled={instructions.length === 1}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Button
              startIcon={<AddIcon />}
              onClick={addInstruction}
              variant="outlined"
              size="small"
            >
              Add Step
            </Button>
          </Grid>

          {/* Photo Upload */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Recipe Photo (Optional)
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadIcon />}
              fullWidth
            >
              {photoFile ? photoFile.name : photoPreview ? 'Change Photo' : 'Upload Photo'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handlePhotoChange}
              />
            </Button>
            {photoPreview && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <img
                  src={photoPreview}
                  alt="Recipe preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    borderRadius: '8px',
                  }}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? (isEditMode ? 'Saving...' : 'Adding Recipe...') : (isEditMode ? 'Save Changes' : 'Add Recipe')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRecipeDialog;
